import { fetchUtils, type DataProvider } from 'react-admin';
import { accessToken } from '../api';
import { IDENTITY_API_URL } from '../config';

// The identity backend's /users, /roles and /features (UserManagement.Admin only), as the SuperTokens version
// served them: list totals in Content-Range, single records at /<resource>/GetOne/<id>, saves as PUT.
async function httpClient (url: string, options: fetchUtils.Options = {}) {
    return fetchUtils.fetchJson(url, { ...options, user: { authenticated: true, token: `Bearer ${await accessToken()}` } });
}

function query (params: Record<string, unknown>) {
    return new URLSearchParams(Object.fromEntries(Object.entries(params).map(([k, v]) => [k, JSON.stringify(v)]))).toString();
}

function total (headers: Headers) {
    return parseInt(headers.get('content-range')?.split('/').pop() ?? '0', 10);
}

export const dataProvider: DataProvider = {
    getList: async (resource, params) => {
        const { page, perPage } = params.pagination ?? { page: 1, perPage: 50 };
        const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };
        const { headers, json } = await httpClient(`${IDENTITY_API_URL}/${resource}?${query({
            sort: [field, order],
            range: [(page - 1) * perPage, page * perPage - 1],
            filter: params.filter
        })}`);
        return { data: json, total: total(headers) };
    },

    getOne: async (resource, params) => {
        const { json } = await httpClient(`${IDENTITY_API_URL}/${resource}/GetOne/${encodeURIComponent(params.id)}`);
        return { data: json };
    },

    getMany: async (resource, params) => {
        const { json } = await httpClient(`${IDENTITY_API_URL}/${resource}?${query({ filter: { id: params.ids } })}`);
        return { data: json };
    },

    getManyReference: async (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const { headers, json } = await httpClient(`${IDENTITY_API_URL}/${resource}?${query({
            sort: [field, order],
            range: [(page - 1) * perPage, page * perPage - 1],
            filter: { ...params.filter, [params.target]: params.id }
        })}`);
        return { data: json, total: total(headers) };
    },

    update: async (resource, params) => {
        const { json } = await httpClient(`${IDENTITY_API_URL}/${resource}/${encodeURIComponent(params.id)}`, {
            method: 'PUT',
            body: JSON.stringify(params.data)
        });
        return { data: { ...json, id: json.id ?? params.id } };
    },

    updateMany: async () => {
        throw new Error('Not supported');
    },

    create: async () => {
        throw new Error('Not supported');
    },

    delete: async (resource, params) => {
        const { json } = await httpClient(`${IDENTITY_API_URL}/${resource}/${encodeURIComponent(params.id)}`, { method: 'DELETE' });
        return { data: json };
    },

    deleteMany: async (resource, params) => {
        await httpClient(`${IDENTITY_API_URL}/${resource}?${query({ filter: { id: params.ids } })}`, { method: 'DELETE' });
        return { data: params.ids };
    }
};
